import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Tabs,
  Tab,
  Avatar,
  Chip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../store";
import {
  fetchReviews,
  createReview,
  updateReview,
  deleteReview,
  fetchReviewSummary,
} from "../store/slices/reviewsSlice";
import { fetchCases } from "../store/slices/casesSlice";
import { fetchClients } from "../store/slices/clientsSlice";
import type {
  ReviewCreateInput,
  ReviewUpdateInput,
} from "../services/reviewsService";
import ReviewSystem from "../components/ReviewSystem";
import { User } from "../services/authService";
import { setError, setLoading } from "../store/slices/documentsSlice";
import usersService from "../services/usersService";

const Reviews: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { cases } = useSelector((state: RootState) => state.cases);
  const { clients } = useSelector((state: RootState) => state.clients);
  const [users, setUsers] = useState<User[]>([]);
  const { reviews, summary, loading, error } = useSelector(
    (state: RootState) => state.newReviews,
  );

  const [activeTab, setActiveTab] = useState(0);
  const [selectedLawyer, setSelectedLawyer] = useState<string | null>(null);
  const [selectedClient, setSelectedClient] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const { user, isAuthenticated } = useSelector(
    (state: RootState) => state.auth,
  );

  const currentUserId = user?.user_id || ''; // Replace with actual user ID from auth

  useEffect(() => {
    // Fetch all data once
    dispatch(fetchReviews());
    dispatch(fetchReviewSummary());
    dispatch(fetchCases({ userId: currentUserId }));
    dispatch(fetchClients(currentUserId));
    loadUsers();
  }, [dispatch, currentUserId]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const users = await usersService.getUsers();
      setUsers(users.data);
    } catch (err: any) {
      const errorMessage = err.message || "Failed to load users";
      setError(errorMessage);
      console.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReview = (data: ReviewCreateInput) => {
    dispatch(createReview(data));
  };

  const handleUpdateReview = (id: number, data: ReviewUpdateInput) => {
    dispatch(updateReview({ reviewId: id, data }));
  };

  const handleDeleteReview = (id: number) => {
    dispatch(deleteReview(id));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setSelectedLawyer(null);
    setSelectedClient(null);
  };

  const filteredLawyers = users?.filter(
    (user: User) =>
      user.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.position?.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const filteredClients = clients.filter((client) =>
    `${client.first_name} ${client.last_name}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase()),
  );

  // Filter reviews based on selected lawyer or client
  const getFilteredReviews = () => {
    if (!reviews || reviews.length === 0) return reviews;
    
    if (selectedLawyer) {
      // Find the selected lawyer's user_id from users array
      const selectedLawyerUser = users.find(u => u.user_id === selectedLawyer);
      
      if (!selectedLawyerUser) {
        return [];
      }
      
      return reviews.filter(review => {
        if (!review.case_file?.lawyers_assigned && !review.case_file?.supervisor) return false;
        
        // Check lawyers_assigned
        let lawyerMatches = false;
        if (review.case_file?.lawyers_assigned) {
          // Parse lawyers_assigned string and check if selected lawyer's user_id is included
          const lawyersAssigned = review.case_file.lawyers_assigned
            .split(',')
            .map((lawyer: string) => lawyer.trim());
          
          // Handle different formats: "69bd7a215c400, 69badf94820b8" or "69badf94820b8: 14997"
          const lawyerIds = lawyersAssigned.map((lawyer: string) => {
            const parts = lawyer.split(':');
            return parts[0].trim(); // Get the user_id part
          });
          
          lawyerMatches = lawyerIds.includes(selectedLawyerUser.user_id);
        }
        
        // Check supervisor
        let supervisorMatches = false;
        if (review.case_file?.supervisor) {
          supervisorMatches = review.case_file.supervisor === selectedLawyerUser.user_id;
        }
        
        // Return true if lawyer is either in lawyers_assigned OR is the supervisor
        return lawyerMatches || supervisorMatches;
      });
    }
    
    if (selectedClient) {
      return reviews.filter(review => 
        review.case_file?.customer_id === selectedClient
      );
    }
    
    return reviews; // No filtering for "All Reviews" tab
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Client Reviews & Testimonials
      </Typography>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="All Reviews" />
        <Tab label="Lawyer Reviews" />
        <Tab label="Client Reviews" />
        <Tab label="Organization Overview" />
      </Tabs>

      {activeTab === 0 && (
        <ReviewSystem
          reviews={getFilteredReviews()}
          summary={summary}
          loading={loading}
          error={error}
          cases={cases}
          clients={clients}
          onCreateReview={handleCreateReview}
          onUpdateReview={handleUpdateReview}
          onDeleteReview={handleDeleteReview}
          organizationView={true}
        />
      )}

      {activeTab === 1 && (
        <Box sx={{ display: "flex", gap: 3 }}>
          <Card sx={{ width: 300, flexShrink: 0 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Select Lawyer
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Search lawyers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchIcon
                      color="action"
                      fontSize="small"
                      sx={{ mr: 1 }}
                    />
                  ),
                }}
                sx={{ mb: 2 }}
              />
              <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                {filteredLawyers?.filter(l => l.role.name !== 'client').map((lawyer: User) => (
                  <Card
                    key={lawyer.id}
                    sx={{
                      mb: 1,
                      cursor: "pointer",
                      border:
                        selectedLawyer === lawyer.user_id
                          ? "2px solid primary.main"
                          : "1px solid grey.200",
                    }}
                    onClick={() => setSelectedLawyer(lawyer.user_id)}
                  >
                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Avatar>
                          <PersonIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">
                            {lawyer.full_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {lawyer.position?.name}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </CardContent>
          </Card>

          <Box sx={{ flex: 1 }}>
            {selectedLawyer ? (
              <ReviewSystem
                reviews={getFilteredReviews()}
                summary={summary}
                loading={loading}
                error={error}
                cases={cases}
                clients={clients}
                onCreateReview={handleCreateReview}
                onUpdateReview={handleUpdateReview}
                onDeleteReview={handleDeleteReview}
                lawyerId={selectedLawyer}
              />
            ) : (
              <Card>
                <CardContent sx={{ textAlign: "center", py: 8 }}>
                  <Typography variant="h6" color="text.secondary">
                    Select a lawyer to view their reviews
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>
      )}

      {activeTab === 2 && (
        <Box sx={{ display: "flex", gap: 3 }}>
          <Card sx={{ width: 300, flexShrink: 0 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Select Client
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchIcon
                      color="action"
                      fontSize="small"
                      sx={{ mr: 1 }}
                    />
                  ),
                }}
                sx={{ mb: 2 }}
              />
              <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                {filteredClients?.map((client) => (
                  <Card
                    key={client.id}
                    sx={{
                      mb: 1,
                      cursor: "pointer",
                      border:
                        selectedClient === client.id
                          ? "2px solid primary.main"
                          : "1px solid grey.200",
                    }}
                    onClick={() => setSelectedClient(client.id)}
                  >
                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Avatar>
                          {client.first_name[0]}
                          {client.last_name[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">
                            {client.first_name} {client.last_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {client.email}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </CardContent>
          </Card>

          <Box sx={{ flex: 1 }}>
            {selectedClient ? (
              <ReviewSystem
                reviews={getFilteredReviews()}
                summary={summary}
                loading={loading}
                error={error}
                cases={cases}
                clients={clients}
                onCreateReview={handleCreateReview}
                onUpdateReview={handleUpdateReview}
                onDeleteReview={handleDeleteReview}
                clientId={selectedClient}
              />
            ) : (
              <Card>
                <CardContent sx={{ textAlign: "center", py: 8 }}>
                  <Typography variant="h6" color="text.secondary">
                    Select a client to view their reviews
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>
      )}

      {activeTab === 3 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Organization Review Overview
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Comprehensive review statistics and performance metrics for the
              entire organization.
            </Typography>

            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              <Chip
                label={`Total Reviews: ${summary?.total_reviews || 0}`}
                color="primary"
              />
              <Chip
                label={`Average Rating: ${summary?.average_lawyer_satisfaction?.toFixed(1) || "0.0"}`}
                color="success"
              />
              <Chip label={`Response Rate: 98%`} color="info" />
              <Chip
                label={`Verified: ${summary?.total_reviews || 0}`}
                color="secondary"
              />
            </Box>

            <ReviewSystem
              reviews={reviews}
              summary={summary}
              loading={loading}
              error={error}
              cases={cases}
              clients={clients}
              onCreateReview={handleCreateReview}
              onUpdateReview={handleUpdateReview}
              onDeleteReview={handleDeleteReview}
              organizationView={true}
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default Reviews;
